import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import Logo from '../Logo/Logo';
import HelpModal from '../Help/HelpModal';
import ReportProblemButton from '../ReportProblemButton';
import VersionBadge from '../VersionBadge';
import { 
  Menu, 
  X, 
  Home, 
  Users, 
  FileText, 
  CreditCard, 
  Upload, 
  Settings,
  MessageSquareWarning,
  BookOpen,
  ListChecks,
  User,
  LogOut,
  BarChart3,
  Package,
  Pill,
  ChevronDown,
  Calculator,
  Landmark,
  DollarSign,
  Database,
  TestTube,
  Building2,
  Percent,
  TrendingDown,
  ChevronUp
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getCompanyProfile, getFeatureFlags } from '../../config/api';
import { usePermissions } from '../../hooks/usePermissionsMock';

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { user, logout, hasRole } = useAuth();
  const [company, setCompany] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  useEffect(() => {
    let alive = true;
    getCompanyProfile().then((c) => { if (alive) setCompany(c); }).catch(() => {});
    getFeatureFlags().then((f) => { if (alive) setFeatureFlags(f || {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const { canAccessNavigationItem, isLoading: permissionsLoading } = usePermissions();
  const [navigationPermissions, setNavigationPermissions] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const dropdownRef = useRef(null);

  // Check permissions for navigation items
  useEffect(() => {
    const checkPermissions = async () => {
      if (permissionsLoading) return;
      
      const permissions = {};
      const permissionKeys = [
        'customers', 'products', 'contracts', 'gpos', 'sales-data',
        'chargebacks', 'upload', 'edi', 'batches', 'reports', 'amp', 'admin', 'test-data'
      ];
      
      for (const key of permissionKeys) {
        permissions[key] = await canAccessNavigationItem(key);
      }
      
      setNavigationPermissions(permissions);
    };
    
    checkPermissions();
  }, [canAccessNavigationItem, permissionsLoading]);

  // Per-module nav icon colors (semantic status icons elsewhere keep red/green/yellow)
  const NAV_ICON_COLORS = {
    'Data Management': 'text-indigo-600',
    'Customers': 'text-blue-600',
    'Products': 'text-emerald-600',
    'Contracts': 'text-amber-600',
    'GPOs': 'text-purple-600',
    'Sales Data': 'text-cyan-600',
    'Processing': 'text-orange-600',
    'Chargebacks': 'text-rose-600',
    'Upload': 'text-sky-600',
    'EDI': 'text-teal-600',
    'EDI Mapping': 'text-teal-700',
    'Batches': 'text-violet-600',
    'Pricing': 'text-fuchsia-600',
    'Analytics': 'text-blue-600',
    'AMP': 'text-fuchsia-600',
    'Gov Pricing': 'text-green-600',
    'IRA Rebates': 'text-lime-600',
    'Rebates': 'text-fuchsia-700',
    'Gross-to-Net': 'text-blue-700',
    'Finance': 'text-emerald-600',
    'Reports': 'text-blue-600',
    'Test Data': 'text-pink-600',
  };

  // Navigation structure with sub-menus
  const navigation = [
    {
      name: 'Data Management',
      icon: Database,
      current: location.pathname.startsWith('/app/customers') ||
               location.pathname.startsWith('/app/products') ||
               location.pathname.startsWith('/app/contracts') ||
               location.pathname.startsWith('/app/gpos') ||
               location.pathname.startsWith('/app/sales-data'),
      permissionKey: 'customers', // Check if user can access any data management
      submenu: [
        { name: 'Customers', href: '/app/customers', icon: Users, current: location.pathname.startsWith('/app/customers'), permissionKey: 'customers' },
        { name: 'Products', href: '/app/products', icon: Pill, current: location.pathname.startsWith('/app/products'), permissionKey: 'products' },
        { name: 'Contracts', href: '/app/contracts', icon: FileText, current: location.pathname.startsWith('/app/contracts'), permissionKey: 'contracts' },
        { name: 'GPOs', href: '/app/gpos', icon: Building2, current: location.pathname.startsWith('/app/gpos'), permissionKey: 'gpos' },
        { name: 'Sales Data', href: '/app/sales-data', icon: Database, current: location.pathname.startsWith('/app/sales-data'), permissionKey: 'sales-data' },
      ]
    },
    {
      name: 'Processing',
      icon: Upload,
      current: location.pathname.startsWith('/app/chargebacks') ||
               location.pathname.startsWith('/app/upload') ||
               location.pathname.startsWith('/app/edi') ||
               location.pathname.startsWith('/app/batches'),
      permissionKey: 'chargebacks', // Check if user can access any processing
      submenu: [
        { name: 'Chargebacks', href: '/app/chargebacks', icon: CreditCard, current: location.pathname.startsWith('/app/chargebacks'), permissionKey: 'chargebacks' },
        { name: 'Upload', href: '/app/upload', icon: Upload, current: location.pathname.startsWith('/app/upload'), permissionKey: 'upload' },
        { name: 'EDI', href: '/app/edi', icon: BarChart3, current: location.pathname === '/app/edi', permissionKey: 'edi' },
        { name: 'EDI Mapping', href: '/app/edi/wholesaler-mapping', icon: Database, current: location.pathname.startsWith('/app/edi/wholesaler-mapping'), permissionKey: 'edi' },
        { name: 'Batches', href: '/app/batches', icon: Package, current: location.pathname.startsWith('/app/batches'), permissionKey: 'batches' },
      ]
    },
    {
      name: 'Pricing',
      icon: Calculator,
      current: location.pathname.startsWith('/app/amp') ||
               location.pathname.startsWith('/app/government-pricing') ||
               location.pathname.startsWith('/app/ira-rebates') ||
               location.pathname.startsWith('/app/rebates'),
      permissionKey: 'amp', // show group if user can access any pricing item
      submenu: [
        { name: 'AMP', href: '/app/amp', icon: Calculator, current: location.pathname.startsWith('/app/amp'), permissionKey: 'amp' },
        { name: 'Gov Pricing', href: '/app/government-pricing', icon: Landmark, current: location.pathname.startsWith('/app/government-pricing'), permissionKey: 'amp' },
        ...((featureFlags?.iraPartB || featureFlags?.iraPartD) ? [
          { name: 'IRA Rebates', href: '/app/ira-rebates', icon: Landmark, current: location.pathname.startsWith('/app/ira-rebates'), permissionKey: 'reports' }
        ] : []),
        { name: 'Rebates', href: '/app/rebates', icon: Percent, current: location.pathname.startsWith('/app/rebates'), permissionKey: 'contracts' },
      ]
    },
    {
      name: 'Analytics',
      icon: BarChart3,
      current: location.pathname.startsWith('/app/gtn') ||
               location.pathname.startsWith('/app/finance') ||
               location.pathname.startsWith('/app/reports'),
      permissionKey: 'reports', // show group if user can access any analytics item
      submenu: [
        { name: 'Gross-to-Net', href: '/app/gtn', icon: TrendingDown, current: location.pathname.startsWith('/app/gtn'), permissionKey: 'reports' },
        { name: 'Finance', href: '/app/finance', icon: DollarSign, current: location.pathname.startsWith('/app/finance'), permissionKey: 'reports' },
        { name: 'Reports', href: '/app/reports', icon: BarChart3, current: location.pathname.startsWith('/app/reports'), permissionKey: 'reports' },
      ]
    },
    {
      name: 'Test Data',
      href: '/app/admin/test-data',
      icon: TestTube,
      current: location.pathname.startsWith('/app/admin/test-data'),
      permissionKey: 'test-data'
    },
  ];

  // Filter navigation based on user permissions
  const filteredNavigation = navigation.filter(item => {
    if (!item.permissionKey) return true; // No permission restriction
    
    // Admin users always see all items
    if (hasRole(['ADMIN'])) {
      return true;
    }

    // Admin-only areas (Admin panel, Test Data) must NEVER show for non-admins,
    // regardless of any permission flag — their routes and backend endpoints are
    // ADMIN-only, so exposing the link just dead-ends non-admins on the dashboard.
    // (Test Data can delete/reset ALL data, so this stays strictly admin-gated.)
    if (item.permissionKey === 'admin' || item.permissionKey === 'test-data') {
      return false;
    }

    // If permissions are still loading, show items based on user role as fallback
    if (permissionsLoading) {
      // Fallback to role-based access while permissions load
      if (item.permissionKey === 'customers') {
        return hasRole(['ADMIN', 'STAFF', 'CUSTOMER']);
      }
      if (item.permissionKey === 'reports' || item.permissionKey === 'amp') {
        return hasRole(['ADMIN', 'STAFF', 'CUSTOMER']);
      }
      if (item.permissionKey === 'chargebacks' || item.permissionKey === 'upload' || 
          item.permissionKey === 'edi' || item.permissionKey === 'batches') {
        return hasRole(['ADMIN', 'STAFF']);
      }
      if (item.permissionKey === 'admin' || item.permissionKey === 'test-data') {
        return hasRole(['ADMIN']);
      }
      return false;
    }
    
    return navigationPermissions[item.permissionKey] || false;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUserMenuClick = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleMenuAction = (action) => {
    setUserMenuOpen(false);
    if (action === 'profile') {
      navigate('/app/profile');
    } else if (action === 'admin') {
      navigate('/app/admin');
    } else if (action === 'problem-reports') {
      navigate('/app/admin/problem-reports');
    } else if (action === 'guide') {
      navigate('/app/guide');
    } else if (action === 'edi-validation') {
      navigate('/app/guide/edi-844-validation');
    } else if (action === 'status-codes') {
      navigate('/app/guide/status-codes');
    } else if (action === 'logout') {
      handleLogout();
    }
  };

  const handleDropdownToggle = (itemName) => {
    setActiveDropdown(activeDropdown === itemName ? null : itemName);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo and Brand */}
            <div className="flex items-center gap-3">
              <Link to="/app/dashboard">
                <Logo variant="color" size="lg" />
              </Link>
              {(company?.name || company?.logo) && (
                <div className="hidden items-center gap-2 border-l border-gray-200 pl-3 sm:flex">
                  {company.logo && <img src={company.logo} alt={company.name || 'Company'} className="h-8 max-w-[120px] object-contain" />}
                  {company.name && <span className="text-sm font-semibold text-gray-700">{company.name}</span>}
                </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-2" ref={dropdownRef}>
              {filteredNavigation.map((item) => (
                <div key={item.name} className="relative">
                  {item.submenu ? (
                    // Dropdown menu
                    <div>
                      <button
                        onClick={() => handleDropdownToggle(item.name)}
                        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          item.current
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={`Toggle ${item.name} menu`}
                      >
                        <item.icon className={`mr-2 h-5 w-5 ${NAV_ICON_COLORS[item.name] || 'text-gray-500'}`} />
                        {item.name}
                        <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${
                          activeDropdown === item.name ? 'rotate-180' : ''
                        }`} />
                      </button>
                      
                      {/* Dropdown content */}
                      {activeDropdown === item.name && (
                        <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                          <div className="py-1">
                            {item.submenu
                              .filter(subItem => {
                                if (!subItem.permissionKey) return true;
                                
                                // Admin users always see all submenu items
                                if (hasRole(['ADMIN'])) {
                                  return true;
                                }
                                
                                // Fallback to role-based access while permissions load
                                if (permissionsLoading) {
                                  if (subItem.permissionKey === 'customers') {
                                    return hasRole(['ADMIN', 'STAFF', 'CUSTOMER']);
                                  }
                                  if (subItem.permissionKey === 'products' || subItem.permissionKey === 'contracts' || 
                                      subItem.permissionKey === 'gpos' || subItem.permissionKey === 'sales-data') {
                                    return hasRole(['ADMIN', 'STAFF']);
                                  }
                                  if (subItem.permissionKey === 'chargebacks' || subItem.permissionKey === 'upload' ||
                                      subItem.permissionKey === 'edi' || subItem.permissionKey === 'batches') {
                                    return hasRole(['ADMIN', 'STAFF']);
                                  }
                                  if (subItem.permissionKey === 'amp' || subItem.permissionKey === 'reports') {
                                    return hasRole(['ADMIN', 'STAFF', 'CUSTOMER']);
                                  }
                                  return false;
                                }
                                
                                return navigationPermissions[subItem.permissionKey] || false;
                              })
                              .map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className={`group flex items-center px-4 py-2 text-sm ${
                                  subItem.current
                                    ? 'bg-primary-100 text-primary-900'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                                onClick={() => setActiveDropdown(null)}
                              >
                                <subItem.icon className={`mr-3 h-4 w-4 ${NAV_ICON_COLORS[subItem.name] || 'text-gray-500'}`} />
                                {subItem.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular link
                    <Link
                      to={item.href}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        item.current
                          ? 'bg-primary-100 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <item.icon className={`mr-2 h-5 w-5 ${NAV_ICON_COLORS[item.name] || 'text-gray-500'}`} />
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={handleUserMenuClick}
                className="flex items-center space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                title="User menu"
              >
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary-600" />
                </div>
                <span className="hidden lg:block text-gray-700">{user?.email}</span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>

              {/* User dropdown menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handleMenuAction('profile')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      title="Profile"
                    >
                      <User className="mr-3 h-4 w-4" />
                      Profile
                    </button>
                    {hasRole(['ADMIN']) && (
                      <button
                        onClick={() => handleMenuAction('admin')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        title="Admin"
                      >
                        <Settings className="mr-3 h-4 w-4" />
                        Admin
                      </button>
                    )}
                    {hasRole(['ADMIN', 'STAFF']) && (
                      <button
                        onClick={() => handleMenuAction('problem-reports')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        title="Problem Reports"
                      >
                        <MessageSquareWarning className="mr-3 h-4 w-4" />
                        Problem Reports
                      </button>
                    )}
                    <button
                      onClick={() => handleMenuAction('guide')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      title="Open the User Guide"
                    >
                      <BookOpen className="mr-3 h-4 w-4" />
                      User Guide
                    </button>
                    <button
                      onClick={() => handleMenuAction('edi-validation')}
                      className="flex items-center w-full px-4 py-2 pl-9 text-sm text-gray-600 hover:bg-gray-100"
                      title="How an EDI 844 is validated, step by step"
                    >
                      <ListChecks className="mr-3 h-4 w-4" />
                      EDI 844 Validation
                    </button>
                    <button
                      onClick={() => handleMenuAction('status-codes')}
                      className="flex items-center w-full px-4 py-2 pl-9 text-sm text-gray-600 hover:bg-gray-100"
                      title="All status codes and what they mean"
                    >
                      <ListChecks className="mr-3 h-4 w-4" />
                      Status Codes
                    </button>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      onClick={() => handleMenuAction('logout')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      title="Logout"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              onClick={() => setMobileMenuOpen(true)}
              title="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`fixed inset-0 z-50 lg:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setMobileMenuOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white shadow-xl">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
            <Link to="/app/dashboard">
              <Logo variant="color" size="sm" />
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-400 hover:text-gray-600"
              title="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-2 px-4 py-6">
            {filteredNavigation.map((item) => (
              <div key={item.name}>
                {item.submenu ? (
                  // Mobile dropdown
                  <div>
                    <button
                      onClick={() => handleDropdownToggle(item.name)}
                      className={`group flex items-center w-full px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                        item.current
                          ? 'bg-primary-100 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      title={`Toggle ${item.name} menu`}
                    >
                      <item.icon className={`mr-3 h-5 w-5 ${NAV_ICON_COLORS[item.name] || 'text-gray-500'}`} />
                      {item.name}
                      <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${
                        activeDropdown === item.name ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {activeDropdown === item.name && (
                      <div className="ml-6 space-y-1 mt-2">
                        {item.submenu
                          .filter(subItem => {
                            if (!subItem.permissionKey) return true;
                            
                            // Admin users always see all submenu items
                            if (hasRole(['ADMIN'])) {
                              return true;
                            }
                            
                            // Fallback to role-based access while permissions load
                            if (permissionsLoading) {
                              if (subItem.permissionKey === 'customers') {
                                return hasRole(['ADMIN', 'STAFF', 'CUSTOMER']);
                              }
                              if (subItem.permissionKey === 'products' || subItem.permissionKey === 'contracts' || 
                                  subItem.permissionKey === 'gpos' || subItem.permissionKey === 'sales-data') {
                                return hasRole(['ADMIN', 'STAFF']);
                              }
                              if (subItem.permissionKey === 'chargebacks' || subItem.permissionKey === 'upload' ||
                                  subItem.permissionKey === 'edi' || subItem.permissionKey === 'batches') {
                                return hasRole(['ADMIN', 'STAFF']);
                              }
                              if (subItem.permissionKey === 'amp' || subItem.permissionKey === 'reports') {
                                return hasRole(['ADMIN', 'STAFF', 'CUSTOMER']);
                              }
                              return false;
                            }
                            
                            return navigationPermissions[subItem.permissionKey] || false;
                          })
                          .map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              subItem.current
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <subItem.icon className={`mr-3 h-4 w-4 ${NAV_ICON_COLORS[subItem.name] || 'text-gray-500'}`} />
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Mobile regular link
                  <Link
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                      item.current
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className={`mr-3 h-5 w-5 ${NAV_ICON_COLORS[item.name] || 'text-gray-500'}`} />
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>
          
          {/* Mobile user menu */}
          <div className="border-t border-gray-200 px-2 py-4">
            <div className="flex items-center space-x-2 mb-3">
              <User className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">{user?.email}</span>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => {
                  navigate('/app/profile');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-2 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-md"
                title="Profile"
              >
                Profile
              </button>
              {hasRole(['ADMIN']) && (
                <button
                  onClick={() => {
                    navigate('/app/admin');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-2 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-md"
                  title="Admin"
                >
                  Admin
                </button>
              )}
              <button
                onClick={() => {
                  navigate('/app/guide');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-2 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-md flex items-center"
                title="Open the User Guide"
              >
                <BookOpen className="mr-2 h-4 w-4" /> User Guide
              </button>
              <button
                onClick={() => {
                  navigate('/app/guide/edi-844-validation');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-2 py-2 pl-7 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-md flex items-center"
                title="How an EDI 844 is validated, step by step"
              >
                <ListChecks className="mr-2 h-4 w-4" /> EDI 844 Validation
              </button>
              <button
                onClick={() => {
                  navigate('/app/guide/status-codes');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-2 py-2 pl-7 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-md flex items-center"
                title="All status codes and what they mean"
              >
                <ListChecks className="mr-2 h-4 w-4" /> Status Codes
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-2 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-md flex items-center"
                title="Logout"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="w-full">
        {/* Page content */}
        <main className="py-6">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Per-screen help — shows a floating "?" on every screen except the dashboard */}
      <HelpModal />

      {/* Global "Report Problem" — captures screen context + user notes for triage */}
      <ReportProblemButton />

      <VersionBadge className="fixed bottom-1 left-2 z-40 hidden sm:block" />
    </div>
  );
};

export default Layout;